import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as GeoFire from 'geofire-common';
import { merge } from 'lodash';
import {
    first,
    forkJoin,
    from,
    mergeMap,
    Observable,
    of,
    ReplaySubject,
    take,
} from 'rxjs';
import { Trainer, User } from './user.types';

@Injectable({
    providedIn: 'root',
})
export class TrainerService {
    private _trainer: ReplaySubject<Trainer> = new ReplaySubject<Trainer>(1);

    constructor(private fireStore: AngularFirestore) {}

    get trainer$(): Observable<Trainer> {
        return this._trainer.asObservable();
    }

    set user(currentUser: User) {
        if (!currentUser) {
            this._trainer.next(null);
            return;
        }
        this.fireStore
            .collection('trainers', (ref) =>
                ref.where('roles.admin', 'array-contains', currentUser.uid)
            )
            .get()
            .pipe(first())
            .subscribe((snapshot) => {
                if (!snapshot.docs[0]) {
                    this._trainer.next(null);
                    return;
                }
                const trainer = snapshot.docs[0].data() as Trainer;
                this._trainer.next({ ...trainer, uid: snapshot.docs[0].id });
            });
    }

    createTrainer(userUid: string, name: string): Observable<void> {
        const newTrainer: Trainer = {
            name: name,
            roles: { admin: [userUid] },
        };
        return from(
            // TODO: set rights in firebase
            this.fireStore.collection('trainers').doc().set(newTrainer)
        );
    }

    save(
        trainerId: string,
        data: Partial<Trainer>,
        cachedData?: Partial<Trainer>
    ): Observable<void> {
        const saveTrainer = from(
            this.fireStore
                .collection('trainers')
                .doc(trainerId)
                .set(data, { merge: true })
        );
        return forkJoin([this.trainer$.pipe(take(1)), saveTrainer]).pipe(
            mergeMap(([trainer]) =>
                of(this._trainer.next(merge(trainer, cachedData || data)))
            )
        );
    }

    searchByLocation(
        latitude: number,
        longitude: number,
        radiusInKm?: number
    ): Observable<any[]> {
        const DEFAULT_DISTANCE_KM = 80;
        const center: GeoFire.Geopoint = [latitude, longitude];
        const radiusInM = (radiusInKm || DEFAULT_DISTANCE_KM) * 1000;
        const bounds = GeoFire.geohashQueryBounds(center, radiusInM);

        const queries = [];
        for (const bound of bounds) {
            const query = this.fireStore
                .collection('trainers')
                .ref.orderBy('location.geo.hash')
                .startAt(bound[0])
                .endAt(bound[1]);
            queries.push(query.get());
        }

        return from(
            Promise.all(queries).then((snapshots) => {
                const matchingDocs = [];
                for (const snap of snapshots) {
                    for (const doc of snap.docs) {
                        // We have to filter out a few false positives due to GeoHash
                        // accuracy, but most will match
                        const distanceInKm = GeoFire.distanceBetween(
                            [
                                doc.get('location.geo.latitude'),
                                doc.get('location.geo.longitude'),
                            ],
                            center
                        );
                        if (
                            distanceInKm <=
                            (doc.get('location.maxDistanceInKm') ||
                                DEFAULT_DISTANCE_KM)
                        ) {
                            matchingDocs.push(doc.data());
                        }
                    }
                }
                return matchingDocs;
            })
        );
    }

    updateTrainer(trainerId: string, data: Partial<Trainer>): Observable<void> {
        return this.save(trainerId, data);
    }
}
